import type { PermissionSet } from '@brimo/shared';
import type { ToolKey, ToolState } from '../lib/session';

/**
 * Navigation model of the shell (Topbar spec in docs/extracts/shell.md A2, routes PLAN.md §8.3), regrouped into
 * areas (D-310…D-314, Nico feedback 28.10.2026, owner decision 29.10.2026): „Start“, „Tasks“, the dropdowns
 * „Kunden“ and „Immobilien“, „Kalender“, „Dialer“, the dropdown „Auswertung“ and „PKV“. Locked tools stay
 * visible with „kein Zugriff“ and open `/locked/<tool>`, except PKV, which only appears when it is usable
 * (D-288); settings live in the profile menu.
 */

export type NavKey =
  | 'home'
  | 'contacts'
  | 'calendar'
  | 'properties'
  | 'tasks'
  | 'deals'
  | 'financing'
  | 'tickets'
  | 'dialer'
  | 'pkv'
  | 'controlling'
  | 'settings'
  | 'more';

export interface NavEntry {
  key: NavKey;
  label: string;
  href: string;
}

/** What the session grants (`SessionInfo.active`): tool access, the permission set, the active modules. */
export interface NavAccess {
  tools: Record<ToolKey, ToolState>;
  permissions: PermissionSet;
  modules: readonly string[];
}

export type NavGroupKey = 'customers' | 'realEstate' | 'analytics';
export type NavLinkKey = 'home' | 'tasks' | 'calendar' | 'dialer' | 'pkv';
export type NavItemKey =
  | 'contacts'
  | 'lists'
  | 'deals'
  | 'financing'
  | 'tickets'
  | 'properties'
  | 'units'
  | 'acquisition'
  | 'figures'
  | 'sales'
  | 'calendarStatistics'
  | 'dialerStatistics';

/** Paths an entry stands for: a path matches itself and everything below it; `query` must match as well. */
interface NavMatch {
  paths: readonly string[];
  query?: { key: string; value: string } | undefined;
}

interface NavItemDefinition extends NavMatch {
  key: NavItemKey;
  label: string;
  /** Second line of the dropdown ("Pipeline und Deal-Akten"). */
  description: string;
  href: string;
  /** Tool gating: a locked tool stays visible („Nicht freigeschaltet“ · „kein Zugriff“) and opens `/locked/<tool>`. */
  tool?: ToolKey | undefined;
  visible?: ((access: NavAccess) => boolean) | undefined;
}

interface NavLinkDefinition extends NavMatch {
  kind: 'link';
  key: NavLinkKey;
  label: string;
  href: string;
  tool?: ToolKey | undefined;
}

interface NavGroupDefinition {
  kind: 'group';
  key: NavGroupKey;
  label: string;
  items: readonly NavItemDefinition[];
  visible?: ((access: NavAccess) => boolean) | undefined;
}

type NavDefinition = NavLinkDefinition | NavGroupDefinition;

/** A dropdown entry or a plain link as the session sees it. */
export interface ResolvedNavItem {
  key: NavItemKey;
  label: string;
  description: string;
  /** `/locked/<tool>` when the tool is locked. */
  href: string;
  locked: boolean;
}

export interface ResolvedNavLink {
  kind: 'link';
  key: NavLinkKey;
  label: string;
  href: string;
  locked: boolean;
}

export interface ResolvedNavGroup {
  kind: 'group';
  key: NavGroupKey;
  label: string;
  items: ResolvedNavItem[];
}

export type ResolvedNavEntry = ResolvedNavLink | ResolvedNavGroup;

/** Tools that appear in the menus only when the session grants them (D-288): an organization without PKV never sees it. */
const hiddenUnlessUsable: ReadonlySet<ToolKey> = new Set<ToolKey>(['pkv']);

/** `controlling.figures` is set (any value but `none`): the strip's permission opens „Auswertung“. */
function hasFigures(access: NavAccess): boolean {
  return access.permissions['controlling.figures'] !== 'none';
}

/**
 * The navigation model, left to right (D-310…D-314). „Start“ (`/home`) is the landing page, „Tasks“ keeps the
 * count badge. „Ankauf“ is the status filter of the property list (`/properties?status=acquisition`).
 */
const navDefinitions: readonly NavDefinition[] = [
  { kind: 'link', key: 'home', label: 'Start', href: '/home', paths: ['/home'] },
  { kind: 'link', key: 'tasks', label: 'Tasks', href: '/tasks', paths: ['/tasks', '/setup/checklist'] },
  {
    kind: 'group',
    key: 'customers',
    label: 'Kunden',
    items: [
      {
        key: 'contacts',
        label: 'Kontakte',
        description: 'Leads und Kunden',
        href: '/contacts',
        paths: ['/contacts'],
      },
      {
        // D-442: the list overview (smart views, campaigns, imports) — the dialer tool guards its routes.
        key: 'lists',
        label: 'Listen',
        description: 'Smart Views, Kampagnen, Importe',
        href: '/dialer/lists',
        paths: ['/dialer/lists'],
        tool: 'dialer',
      },
      {
        key: 'deals',
        label: 'Deals',
        description: 'Pipeline und Deal-Akten',
        href: '/deals',
        paths: ['/deals', '/locked/deals'],
        tool: 'deals',
      },
      {
        key: 'financing',
        label: 'Finanzierungen',
        description: 'Anfragen und Angebote',
        href: '/financing',
        paths: ['/financing', '/locked/financing'],
        tool: 'financing',
      },
      {
        key: 'tickets',
        label: 'Tickets',
        description: 'Support-Postfach',
        href: '/tickets',
        paths: ['/tickets', '/locked/tickets'],
        tool: 'tickets',
      },
    ],
  },
  {
    kind: 'group',
    key: 'realEstate',
    label: 'Immobilien',
    items: [
      {
        key: 'properties',
        label: 'Objekte',
        description: 'Bestand im Vertrieb',
        href: '/properties',
        paths: ['/properties'],
      },
      {
        key: 'units',
        label: 'Alle Einheiten',
        description: 'Einheiten aller Objekte',
        href: '/properties/units',
        paths: ['/properties/units'],
      },
      {
        key: 'acquisition',
        label: 'Ankauf',
        description: 'Ankaufsprüfung und Kalkulation',
        href: '/properties?status=acquisition',
        paths: ['/properties'],
        query: { key: 'status', value: 'acquisition' },
        visible: (access) =>
          access.permissions['properties.acquisition'] === 'yes' &&
          access.modules.includes('property_acquisition'),
      },
    ],
  },
  { kind: 'link', key: 'calendar', label: 'Kalender', href: '/calendar', paths: ['/calendar'] },
  {
    kind: 'link',
    key: 'dialer',
    label: 'Dialer',
    href: '/dialer',
    paths: ['/dialer', '/locked/dialer'],
    tool: 'dialer',
  },
  {
    kind: 'group',
    key: 'analytics',
    label: 'Auswertung',
    visible: hasFigures,
    items: [
      {
        key: 'figures',
        label: 'Kennzahlen',
        description: 'Funnel, Kampagnen, Partner',
        href: '/controlling',
        paths: ['/controlling'],
      },
      {
        key: 'sales',
        label: 'Vertrieb',
        description: 'Termine und Abschlüsse je Partner',
        href: '/controlling/sales',
        paths: ['/controlling/sales'],
      },
      {
        key: 'calendarStatistics',
        label: 'Kalender-Statistik',
        description: 'Buchungen und Show-Quote',
        href: '/calendar/statistics',
        paths: ['/calendar/statistics'],
        // The calendar statistics answer 403 for the back-office reading `tickets` (appointments/statistics.ts).
        visible: (access) => access.permissions['controlling.figures'] !== 'tickets',
      },
      {
        key: 'dialerStatistics',
        label: 'Dialer-Statistik',
        description: 'Anrufe und Ergebnisse je Setter',
        href: '/dialer/statistics',
        paths: ['/dialer/statistics'],
        tool: 'dialer',
      },
    ],
  },
  { kind: 'link', key: 'pkv', label: 'PKV', href: '/pkv', paths: ['/pkv', '/locked/pkv'], tool: 'pkv' },
];

export const toolKeys: readonly ToolKey[] = ['deals', 'financing', 'tickets', 'dialer', 'pkv'];

export function isToolKey(value: string): value is ToolKey {
  return (toolKeys as readonly string[]).includes(value);
}

/** A tool entry is shown unless it is hidden until usable (PKV) and not usable. */
function toolShown(tool: ToolKey | undefined, access: NavAccess): boolean {
  return tool === undefined || !hiddenUnlessUsable.has(tool) || access.tools[tool] === 'ok';
}

function isLocked(tool: ToolKey | undefined, access: NavAccess): boolean {
  return tool !== undefined && access.tools[tool] !== 'ok';
}

function resolveItem(item: NavItemDefinition, access: NavAccess): ResolvedNavItem | null {
  if (!toolShown(item.tool, access) || (item.visible && !item.visible(access))) return null;
  const locked = isLocked(item.tool, access);
  return {
    key: item.key,
    label: item.label,
    description: item.description,
    href: locked && item.tool ? `/locked/${item.tool}` : item.href,
    locked,
  };
}

/** The top-level entries of this session in order; a group without a visible entry is left out. */
export function navigationFor(access: NavAccess): ResolvedNavEntry[] {
  const result: ResolvedNavEntry[] = [];
  for (const entry of navDefinitions) {
    if (entry.kind === 'link') {
      if (!toolShown(entry.tool, access)) continue;
      const locked = isLocked(entry.tool, access);
      result.push({
        kind: 'link',
        key: entry.key,
        label: entry.label,
        href: locked && entry.tool ? `/locked/${entry.tool}` : entry.href,
        locked,
      });
      continue;
    }
    if (entry.visible && !entry.visible(access)) continue;
    const items = entry.items
      .map((item) => resolveItem(item, access))
      .filter((item): item is ResolvedNavItem => item !== null);
    if (items.length > 0) result.push({ kind: 'group', key: entry.key, label: entry.label, items });
  }
  return result;
}

/** Match strength of a path against an entry: the longest matching path, a matching query on top; -1 = none. */
function matchScore(match: NavMatch, pathname: string, params: URLSearchParams): number {
  let best = -1;
  for (const path of match.paths) {
    if (pathname === path || pathname.startsWith(`${path}/`)) best = Math.max(best, path.length);
  }
  if (best < 0 || match.query === undefined) return best;
  return params.get(match.query.key) === match.query.value ? best + 0.5 : -1;
}

export interface ActiveNavigation {
  /** The active top-level entry (a link or a group), or null outside the navigation. */
  top: NavLinkKey | NavGroupKey | null;
  /** The active dropdown entry when `top` is a group. */
  item: NavItemKey | null;
}

/**
 * The entry a location belongs to: the most specific match wins, so `/calendar/statistics` is „Auswertung ›
 * Kalender-Statistik“, `/properties?status=acquisition` „Immobilien › Ankauf“ and `/locked/deals` „Kunden › Deals“.
 */
export function activeNavigation(pathname: string, search = ''): ActiveNavigation {
  const params = new URLSearchParams(search);
  let best: ActiveNavigation = { top: null, item: null };
  let bestScore = -1;
  for (const entry of navDefinitions) {
    if (entry.kind === 'link') {
      const score = matchScore(entry, pathname, params);
      if (score > bestScore) {
        bestScore = score;
        best = { top: entry.key, item: null };
      }
      continue;
    }
    for (const item of entry.items) {
      const score = matchScore(item, pathname, params);
      if (score > bestScore) {
        bestScore = score;
        best = { top: entry.key, item: item.key };
      }
    }
  }
  return best;
}

export interface MoreEntry {
  key: NavItemKey | NavLinkKey | 'settings';
  label: string;
  href: string;
  locked: boolean;
}

export interface MoreSection {
  /** Eyebrow of the section; the first section („Tasks“) has none. */
  eyebrow: string | null;
  entries: MoreEntry[];
}

/** Entries that are tabs of the mobile tab bar and therefore not repeated on „Mehr“. */
const mobileTabItems: ReadonlySet<string> = new Set(['contacts', 'properties']);

/**
 * The „Mehr“ page (Shell 10, regrouped D-314): „Tasks“ first, then „Kunden“, „Immobilien“, „Auswertung“ without the
 * entries that are tabs, then „Werkzeuge“ (Dialer, PKV, Einstellungen). Empty sections are left out.
 */
export function moreSections(access: NavAccess): MoreSection[] {
  const navigation = navigationFor(access);
  const tools: MoreEntry[] = [];
  const sections: MoreSection[] = [
    { eyebrow: null, entries: [{ key: 'tasks', label: 'Tasks', href: '/tasks', locked: false }] },
  ];
  for (const entry of navigation) {
    if (entry.kind === 'group') {
      const entries = entry.items
        .filter((item) => !mobileTabItems.has(item.key))
        .map((item) => ({ key: item.key, label: item.label, href: item.href, locked: item.locked }));
      if (entries.length > 0) sections.push({ eyebrow: entry.label, entries });
    } else if (entry.key === 'dialer' || entry.key === 'pkv') {
      tools.push({ key: entry.key, label: entry.label, href: entry.href, locked: entry.locked });
    }
  }
  tools.push({ key: 'settings', label: 'Einstellungen', href: '/settings', locked: false });
  sections.push({ eyebrow: 'Werkzeuge', entries: tools });
  return sections;
}

/** Labels of every module, used by the mobile header, the empty pages and the locked page. */
export const moduleLabels: Record<NavKey, string> = {
  home: 'Start',
  contacts: 'Kontakte',
  calendar: 'Kalender',
  properties: 'Objekte',
  tasks: 'Meine Tasks',
  deals: 'Deals',
  financing: 'Finanzierungen',
  tickets: 'Tickets',
  dialer: 'Dialer',
  pkv: 'PKV',
  controlling: 'Controlling',
  settings: 'Einstellungen',
  more: 'Mehr',
};

const routeKeys: ReadonlyArray<[prefix: string, key: NavKey]> = [
  ['/home', 'home'],
  ['/contacts', 'contacts'],
  ['/calendar', 'calendar'],
  ['/properties', 'properties'],
  ['/tasks', 'tasks'],
  // The onboarding checklist (Admin 11, Nutzer 13–15) belongs to „Tasks“ (Topbar active="Tasks").
  ['/setup/checklist', 'tasks'],
  ['/deals', 'deals'],
  ['/financing', 'financing'],
  ['/tickets', 'tickets'],
  // „Kontakte · Listen" (D-442): the lists live under /dialer but belong to the contacts on mobile.
  ['/dialer/lists', 'contacts'],
  ['/dialer', 'dialer'],
  ['/pkv', 'pkv'],
  ['/controlling', 'controlling'],
  ['/settings', 'settings'],
  ['/more', 'more'],
];

/** The module a pathname belongs to (`/locked/dialer` counts as the dialer): mobile title and tab, settings state. */
export function navKeyForPath(pathname: string): NavKey | null {
  if (pathname.startsWith('/locked/')) {
    const tool = pathname.slice('/locked/'.length).split('/')[0] ?? '';
    return isToolKey(tool) ? tool : null;
  }
  for (const [prefix, key] of routeKeys) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return key;
  }
  return null;
}

/** Mobile tab bar (Shell 10, D-314): the same five tabs for every role; „Tasks“ lives on „Mehr“. */
export const mobileTabs: readonly NavEntry[] = [
  { key: 'home', label: 'Start', href: '/home' },
  { key: 'contacts', label: 'Kontakte', href: '/contacts' },
  { key: 'calendar', label: 'Kalender', href: '/calendar' },
  { key: 'properties', label: 'Objekte', href: '/properties' },
  { key: 'more', label: 'Mehr', href: '/more' },
];

/** Texts of the locked page (Shell 13); `{admin}` is replaced by the approval recipient's name. */
export const lockedTexts: Record<ToolKey, { headline: string; description: string }> = {
  dialer: {
    headline: 'Der Dialer ist für Sie nicht freigeschaltet.',
    description:
      'Mit dem Dialer rufen Setter und Berater Kontakte aus Listen und Kampagnen an. Freischalten kann {admin} als Org-Admin.',
  },
  tickets: {
    headline: 'Die Tickets sind für Sie nicht freigeschaltet.',
    description:
      'In den Tickets bearbeiten Backoffice und Admins Anfragen aus dem Support-Postfach und dem Mandantenportal. Freischalten kann {admin} als Org-Admin.',
  },
  deals: {
    headline: 'Die Deals sind für Sie nicht freigeschaltet.',
    description:
      'In den Deals führen Berater und Backoffice die Pipeline und die Deal-Akten. Freischalten kann {admin} als Org-Admin.',
  },
  financing: {
    headline: 'Die Finanzierungen sind für Sie nicht freigeschaltet.',
    description:
      'In den Finanzierungen bearbeiten Berater und Finanzierer Anfragen und Angebote. Freischalten kann {admin} als Org-Admin.',
  },
  pkv: {
    headline: 'PKV ist für Ihre Organisation nicht freigeschaltet.',
    description:
      'In PKV bearbeiten Berater und Backoffice Risikovoranfragen, den Rechner und Anträge der privaten Krankenversicherung. Freischalten kann {admin} als Org-Admin.',
  },
};

/** Headline of the locked page when the module is on but the user lacks the right (D-288); the others keep theirs. */
export const lockedPermissionHeadlines: Partial<Record<ToolKey, string>> = {
  pkv: 'Dafür fehlt Ihnen die Berechtigung.',
};

export interface EmptyPageText {
  eyebrow: string;
  headline: string;
  description: string;
  primary: { label: string; href: string };
  secondary: { label: string; href: string };
}

/** Empty states per module in the pattern of Shell 14 (Tickets is the screen's own text). */
export const emptyTexts: Record<Exclude<NavKey, 'home' | 'settings' | 'more' | 'pkv'>, EmptyPageText> = {
  tickets: {
    eyebrow: 'Tickets',
    headline: 'Noch keine Tickets.',
    description:
      'Anfragen aus dem Support-Postfach und dem Mandantenportal landen hier. Verbinden Sie ein Postfach oder legen Sie ein Ticket von Hand an.',
    primary: { label: 'Postfach verbinden', href: '/settings/connections' },
    secondary: { label: 'Ticket anlegen', href: '/tickets/new' },
  },
  contacts: {
    eyebrow: 'Kontakte',
    headline: 'Noch keine Kontakte.',
    description:
      'Leads aus Kampagnen, dem Dialer und der Buchungsseite landen hier. Importieren Sie eine Liste oder legen Sie einen Kontakt von Hand an.',
    primary: { label: 'Kontakt anlegen', href: '/contacts/new' },
    secondary: { label: 'Zurück zu Tasks', href: '/tasks' },
  },
  calendar: {
    eyebrow: 'Kalender',
    headline: 'Noch keine Termine.',
    description:
      'Buchungen über Ihre Buchungsseite und intern vereinbarte Termine landen hier. Legen Sie Termintypen an oder buchen Sie einen Termin von Hand.',
    primary: { label: 'Termin buchen', href: '/calendar/new' },
    secondary: { label: 'Termintypen', href: '/calendar/event-types' },
  },
  properties: {
    eyebrow: 'Objekte',
    headline: 'Noch keine Objekte.',
    description:
      'Objekte aus dem Ankauf und Bauträger-Uploads landen hier. Legen Sie ein Objekt an oder senden Sie einem Bauträger den Upload-Link.',
    primary: { label: 'Objekt anlegen', href: '/properties/new' },
    secondary: { label: 'Zurück zu Tasks', href: '/tasks' },
  },
  tasks: {
    eyebrow: 'Tasks',
    headline: 'Noch keine Tasks.',
    description:
      'Tasks aus Ihren Kontakten, Deals und Terminen landen hier. Starten Sie die Queue oder legen Sie eine Task von Hand an.',
    primary: { label: 'Queue starten', href: '/tasks/queue/start' },
    secondary: { label: 'Task anlegen', href: '/tasks/new' },
  },
  deals: {
    eyebrow: 'Deals',
    headline: 'Noch keine Deals.',
    description:
      'Deals aus Beratungsterminen und Reservierungen landen hier. Buchen Sie eine Beratung oder legen Sie einen Deal von Hand an.',
    primary: { label: 'Deal anlegen', href: '/deals/new' },
    secondary: { label: 'Zurück zu Tasks', href: '/tasks' },
  },
  financing: {
    eyebrow: 'Finanzierungen',
    headline: 'Noch keine Finanzierungen.',
    description:
      'Anfragen aus Deals und Selbstauskünften landen hier. Legen Sie eine Finanzierung an oder weisen Sie einen Finanzierer zu.',
    primary: { label: 'Finanzierung anlegen', href: '/financing/new' },
    secondary: { label: 'Zurück zu Tasks', href: '/tasks' },
  },
  dialer: {
    eyebrow: 'Dialer',
    headline: 'Noch keine Listen.',
    description:
      'Anruflisten aus Kontakten und Kampagnen landen hier. Legen Sie eine Liste an oder starten Sie eine Session aus Ihren Tasks.',
    primary: { label: 'Liste anlegen', href: '/dialer/lists' },
    secondary: { label: 'Zurück zu Tasks', href: '/tasks' },
  },
  controlling: {
    eyebrow: 'Controlling',
    headline: 'Noch keine Kennzahlen.',
    description:
      'Kennzahlen aus Anrufen, Terminen und Deals landen hier, sobald die ersten Daten vorliegen. Starten Sie mit Ihren Tasks oder dem Dialer.',
    primary: { label: 'Zu Meine Tasks', href: '/tasks' },
    secondary: { label: 'Dialer öffnen', href: '/dialer' },
  },
};
